import { Button, Loader } from '@aws-amplify/ui-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { TbArrowRight } from 'react-icons/tb';
import { Link, useNavigate } from 'react-router-dom';
import Banner from '../../custom-components/Banner';
import { ExportTarget, FieldMappingsEditor } from '../../custom-components/FieldMappingsEditor';
import LoadingBars from '../../custom-components/LoadingBars';
import NothingHere from '../../custom-components/NothingHere';
import { toastV4Error, v4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { ExportContext } from '../ExportDetail';
import { EXPORT_ENTITIES, EXPORT_TARGETS } from './exportShapes';

export function ExportStepFields() {
  const exportContext = useContext(ExportContext);
  const navigate = useNavigate();

  const saving = exportContext?.isSaving ?? false;
  const setSaving = exportContext?.setIsSaving ?? (() => {});

  // Reset saving state on mount (in case previous step navigated while saving)
  useEffect(() => {
    exportContext?.setIsSaving(false);
  }, []);

  const mappings = exportContext?.fieldMappings ?? [];
  const loadingFields = exportContext?.fieldMappingsLoading ?? false;
  const setMappings = exportContext?.setFieldMappings ?? (() => {});
  const refreshFieldMappings = exportContext?.refreshFieldMappings;
  const [hasTypeErrors, setHasTypeErrors] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshMappings = async () => {
    if (!refreshFieldMappings || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshFieldMappings();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleValidationChange = useCallback((hasErrors: boolean) => {
    setHasTypeErrors(hasErrors);
  }, []);

  if (!exportContext) {
    return (
      <div className="content content-1200">
        <div className="export-step">
          <LoadingBars numberOfRows={5} />
        </div>
      </div>
    );
  }

  const isSelectable = ['TARGET_SELECTION', 'FIELDS_SELECTION', 'OPTIONS_SELECTION'].includes(exportContext.exportData?.status);
  const contextReadOnly = exportContext.readOnly ?? false;
  const isCrmTarget = exportContext.exportData?.target === 'HUBSPOT' || exportContext.exportData?.target === 'SALESFORCE';
  // For CRM targets, field selection is allowed but target mappings are read-only (managed in Settings)
  const isDisabled = !isSelectable || contextReadOnly;

  const getDestinationLabel = (target?: string): string => {
    switch (target) {
      case 'HUBSPOT': return 'HubSpot';
      case 'SALESFORCE': return 'Salesforce';
      case 'CSV': return 'your CSV file';
      case 'JSON': return 'your JSON file';
      default: return 'your destination';
    }
  };

  const getSettingsLink = (target?: string): string => {
    switch (target) {
      case 'HUBSPOT': return '/settings/integrations/hubspot';
      case 'SALESFORCE': return '/settings/integrations/salesforce';
      default: return '/settings/integrations';
    }
  };

  // Validation: at least one field selected, and all visible fields have target names
  const hasNoVisibleFields = !mappings.some(m => m.visible);
  const hasEmptyTargetName = mappings.some(m => m.visible && !m.target?.trim());

  const handleContinue = async () => {
    setSaving(true);
    setValidationError(null);
    try {
      // Save the mappings as a template (the same mappings return the template already saved),
      // then point the export at it. Only the selection is sent: label, datatype and example are
      // the field's own.
      const team = { team: exportContext.teamId };
      const { template } = await eduleadV4.templates.create(
        {
          request_id: crypto.randomUUID(),
          body: {
            entity: EXPORT_ENTITIES[exportContext.exportData.entity],
            target: EXPORT_TARGETS[exportContext.exportData.target],
            mappings: mappings.map(m => ({
              source: m.source,
              target: m.target,
              order: m.order,
              visible: m.visible
            }))
          }
        },
        team
      );
      await eduleadV4.exports.update(
        { id: exportContext.exportId, update_mask: 'template_id', body: { template_id: template?.id } },
        team
      );
      await exportContext.refreshExport();
      navigate(`/exports/${exportContext.exportId}/options`);
    } catch (error) {
      setValidationError(v4Error(error).message || 'Failed to save fields');
      toastV4Error(error, 'Failed to save fields');
      setSaving(false);
    }
  };

  return (
    <div className="content content-1200">
      <div className="export-step">
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {isCrmTarget
            ? <>
                <span>These fields will be exported to {getDestinationLabel(exportContext.exportData?.target)}. Configure <Link to={getSettingsLink(exportContext.exportData?.target)} target="_blank" rel="noopener noreferrer">field mappings</Link> in Settings.</span>
                <Button
                  className="tiny"
                  onClick={handleRefreshMappings}
                  isDisabled={isRefreshing || loadingFields || !isSelectable}
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </>
            : `Choose which fields to include and map them to ${getDestinationLabel(exportContext.exportData?.target)}.`
          }
        </div>
        {validationError && (
          <Banner color="yellow">{validationError}</Banner>
        )}
        {loadingFields ? (
          <LoadingBars numberOfRows={5} />
        ) : mappings.length > 0 ? (
          <FieldMappingsEditor
            mappings={mappings}
            onChange={setMappings}
            disabled={isDisabled}
            target={exportContext.exportData?.target as ExportTarget}
            onValidationChange={handleValidationChange}
          />
        ) : (
          <NothingHere text={exportContext.exportData?.target
            ? "No field mappings available for this export type."
            : "No target selected yet. Please select a target first."
          } />
        )}
      </div>
      {isSelectable && !contextReadOnly && (
        <div className="export-step__actions">
          {saving && <Loader size="small" />}
          <Button
            variation="primary"
            size="small"
            onClick={handleContinue}
            isDisabled={saving || hasNoVisibleFields || hasEmptyTargetName || hasTypeErrors}
          >
            Save and Continue<TbArrowRight style={{ marginLeft: 8 }} />
          </Button>
        </div>
      )}
    </div>
  );
}
